/**
 * API Client
 * 모든 API 호출을 위한 중앙 집중식 fetch wrapper
 * SSE 스트리밍 지원
 */

import type { ApiResponse } from '@/lib/types/api';

class ApiClient {
  private baseUrl: string;
  
  constructor() {
    this.baseUrl = process.env.NEXT_PUBLIC_FRONT_API_URL 
      ? `${process.env.NEXT_PUBLIC_FRONT_API_URI}` 
      : '/api/v1';
  }
  
  /**
   * 공통 request 메서드
   * @private
   */
  private async request<T>(
    endpoint: string, 
    options?: RequestInit
  ): Promise<ApiResponse<T>> {
    const url = `${this.baseUrl}${endpoint}`;
    
    const config: RequestInit = {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      ...options,
    };
    
    try {
      const response = await fetch(url, config);
      const data = await response.json();
      
      // HTTP 에러 응답도 JSON으로 파싱하여 반환
      if (!response.ok) {
        return {
          code: 'ERROR',
          message: data.message || '요청 실패',
          details: data.details || { status: response.status },
        };
      }
      
      return data as ApiResponse<T>;
    } catch (error) {
      // 네트워크 에러 또는 JSON 파싱 에러
      console.error('[API Client] Request failed:', error);
      return {
        code: 'ERROR',
        message: error instanceof Error ? error.message : '네트워크 오류',
        details: { error: String(error) },
      };
    }
  }
  
  /**
   * GET 요청
   */
  async get<T>(endpoint: string, options?: RequestInit): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { ...options, method: 'GET' });
  }
  
  /**
   * POST 요청
   */
  async post<T>(
    endpoint: string, 
    body?: unknown, 
    options?: RequestInit
  ): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    });
  }
  
  /**
   * PUT 요청
   */
  async put<T>(
    endpoint: string, 
    body?: unknown, 
    options?: RequestInit
  ): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    });
  }
  
  /**
   * DELETE 요청
   */
  async delete<T>(endpoint: string, options?: RequestInit): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { ...options, method: 'DELETE' });
  }
  
  /**
   * SSE 스트리밍 요청
   * Server-Sent Events를 위한 특수 처리
   */
  async stream(endpoint: string, body?: unknown): Promise<Response> {
    const url = `${this.baseUrl}${endpoint}`;
    
    return fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'text/event-stream',
      },
      credentials: 'include',
      body: body ? JSON.stringify(body) : undefined,
    });
  }
}

// 싱글톤 인스턴스 export
export const apiClient = new ApiClient();

